import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import SiteFooterLinks from '../components/SiteFooterLinks';

const heroImage = '/about/about-hero-1280.webp';
const heroImageSrcSet = '/about/about-hero-1280.webp 1280w, /about/about-hero-1750.webp 1750w';
const breakerOneImage = '/about/about-breaker-1-1280.webp';
const breakerOneImageSrcSet = '/about/about-breaker-1-1280.webp 1280w, /about/about-breaker-1-1920.webp 1920w';
const breakerTwoImage = '/about/about-breaker-2-1200.webp';
const storyPortraitImage = '/about/about-story-portrait-600.webp';
const storyPortraitImageSrcSet = '/about/about-story-portrait-600.webp 600w, /about/about-story-portrait-928.webp 928w';

const howItsBuiltSlides = [
  'alMATHEMA uses free-response questions to assess atomic algebra skills, the foundational steps that underlie higher-level math. Targeted follow-up questions are used when needed to clarify student thinking, ensuring results reflect underlying reasoning gaps.The result is a clear picture of which skills are solid and which need attention. No scores.',
  'Results are prioritized by relevance without being overwhelming. Students receive curated resources aligned to each skill so they can take action immediately. Progress is tracked through the student portal, giving students a clear view of their improvement over time.',
  'Educators and advisors see where students stand at both the individual and class level, enabling earlier, more targeted intervention without additional grading or changes to course structure. Access is provided through the educator dashboard.',
];

function AboutUsPage() {
  const theme = useTheme();
  const { almathema } = theme.palette;
  const [activeHowItsBuiltSlide, setActiveHowItsBuiltSlide] = useState(0);

  const handlePrevHowItsBuiltSlide = () => {
    setActiveHowItsBuiltSlide((currentSlide) =>
      currentSlide === 0 ? howItsBuiltSlides.length - 1 : currentSlide - 1,
    );
  };

  const handleNextHowItsBuiltSlide = () => {
    setActiveHowItsBuiltSlide((currentSlide) =>
      currentSlide === howItsBuiltSlides.length - 1 ? 0 : currentSlide + 1,
    );
  };

  return (
    <Box sx={{ backgroundColor: almathema.white }}>
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 460, md: 700 },
          color: almathema.white,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={heroImage}
          srcSet={heroImageSrcSet}
          sizes="100vw"
          alt="Students studying around a table"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.45)' }} />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            pb: { xs: 6, md: 10 },
          }}
        >
          <Stack spacing={2.5} sx={{ maxWidth: 680, mt: { xs: 4, md: 8 } }}>
            <Typography
              sx={{
                color: almathema.sunYellow,
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic',
                fontSize: { xs: '2.6rem', md: '4rem' },
                lineHeight: 1,
                mt: { xs: -1.2, md: -1.8 },
              }}
            >
              About Us
            </Typography>
            <Typography sx={{ color: almathema.white, fontSize: '1rem', maxWidth: 420 }}>
              Making gaps in math understanding visible so students and educators can act with clarity.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack spacing={4}>
            <Typography variant="h3" sx={{ color: almathema.babyBlueIce, fontStyle: 'italic' }}>
              Not a Score. A Blueprint.
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={5} alignItems="flex-start">
              <Typography
                sx={{
                  flex: 1,
                  color: almathema.sapphire,
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  lineHeight: 1.1,
                }}
              >
                Revealing each student's mathematical foundation.
              </Typography>
              <Typography sx={{ flex: 1, color: almathema.sapphire }}>
                alMATHEMA Education Inc. is a nonprofit organization founded on the belief that gaps in math understanding should be visible. When those gaps are clear, students and educators can address them with confidence. We develop a diagnostic system grounded in educators’ direct observations of student thinking, identifying patterns of error and misconception that often remain implicit, and translating them into actionable insight.
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ height: { xs: 280, md: 520 }, overflow: 'hidden' }}>
        <Box
          component="img"
          src={breakerOneImage}
          srcSet={breakerOneImageSrcSet}
          sizes="100vw"
          alt="Students walking on campus"
          loading="lazy"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
        />
      </Box>

      <Box sx={{ backgroundColor: almathema.sapphire, color: almathema.white, py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={5}>
            <Typography sx={{ color: almathema.sunYellow, fontFamily: 'Gill Sans, Lato, sans-serif', fontWeight: 700, fontSize: '1.25rem' }}>
              The problem is not ability.
              <br />
              <span style={{ color: almathema.babyBlueIce }}>It is that gaps in foundational understanding remain invisible.</span>
            </Typography>
            <Box>
              <Typography sx={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: { xs: '2.2rem', md: '3rem' }, lineHeight: 1.1 }}>
                When those gaps go unaddressed, students move forward without the foundation they need to succeed.
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: almathema.white }}>
        <Container maxWidth="md">
          <Stack spacing={5} alignItems="center">
            <Typography variant="h3" sx={{ color: almathema.sapphire, fontStyle: 'italic', textAlign: 'center' }}>
              How It's Built
            </Typography>
            <Typography sx={{ color: almathema.sapphire, fontSize: '1.25rem', textAlign: 'center', maxWidth: 560 }}>
              We make gaps in foundational understanding visible and actionable.
            </Typography>
            <Box
              sx={{
                backgroundColor: almathema.babyBlueIce,
                width: '100%',
                px: { xs: 3, md: 9 },
                py: { xs: 4, md: 9 },
              }}
            >
              <Stack spacing={{ xs: 5, md: 10 }} alignItems="center" justifyContent="center">
                <Stack spacing={4} alignItems="center" sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      color: almathema.sapphire,
                      fontFamily: 'Georgia, serif',
                      fontStyle: 'italic',
                      fontSize: { xs: '1.45rem', md: '2rem' },
                      lineHeight: 1.2,
                      textAlign: 'center',
                      maxWidth: 740,
                    }}
                  >
                    {howItsBuiltSlides[activeHowItsBuiltSlide]}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={2.5} alignItems="center" justifyContent="center">
                  <Box
                    component="button"
                    type="button"
                    onClick={handlePrevHowItsBuiltSlide}
                    aria-label="Go to previous card"
                    sx={{
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: almathema.sapphire,
                      fontSize: '2rem',
                      lineHeight: 1,
                      p: 0,
                      cursor: 'pointer',
                    }}
                  >
                    &larr;
                  </Box>

                  <Stack direction="row" spacing={1.25} alignItems="center">
                    {howItsBuiltSlides.map((_, slideIndex) => (
                      <Box
                        key={`how-its-built-dot-${slideIndex}`}
                        component="button"
                        type="button"
                        onClick={() => setActiveHowItsBuiltSlide(slideIndex)}
                        aria-label={`Go to card ${slideIndex + 1}`}
                        sx={{
                          width: '0.6389rem',
                          height: '0.6389rem',
                          borderRadius: '50%',
                          border: 'none',
                          backgroundColor: slideIndex === activeHowItsBuiltSlide ? almathema.sapphire : '#B9D0ED',
                          cursor: 'pointer',
                          p: 0,
                        }}
                      />
                    ))}
                  </Stack>

                  <Box
                    component="button"
                    type="button"
                    onClick={handleNextHowItsBuiltSlide}
                    aria-label="Go to next card"
                    sx={{
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: almathema.sapphire,
                      fontSize: '2rem',
                      lineHeight: 1,
                      p: 0,
                      cursor: 'pointer',
                    }}
                  >
                    &rarr;
                  </Box>
                </Stack>
              </Stack>
            </Box>
            <Typography variant="h3" sx={{ color: almathema.sapphire, fontStyle: 'italic', textAlign: 'center' }}>
              Where It Fits
            </Typography>
            <Typography sx={{
              color: almathema.sapphire,
              textAlign: 'center',
              maxWidth: 700,
              mt: '0 !important',
            }}>
              alMATHEMA supports key moments across the learning process.
            </Typography>
            <Typography sx={{ color: almathema.sapphire, textAlign: 'center', maxWidth: 700 }}>
              <span style={{ color: almathema.babyBlueIce, fontWeight: 700 }}>Before a course begins</span>
              <br />
              Students and educators gain a clear view of foundational readiness before new content is introduced.
              <br />
              <br />
              <span style={{ color: almathema.babyBlueIce, fontWeight: 700 }}>Before major assessments</span>
              <br />
              Students identify and address specific skill gaps in advance, while educators see where to focus their support.
              <br />
              <br />
              <span style={{ color: almathema.babyBlueIce, fontWeight: 700 }}>At critical transition points</span>
              <br />
              Diagnostics can be used between courses, programs, or key academic milestones where skill gaps may otherwise go undetected.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: almathema.babyBlueIce, py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={5} alignItems="center">
            <Box sx={{ width: { xs: '100%', md: 380 }, height: { xs: 420, md: 500 }, overflow: 'hidden' }}>
              <Box
                component="img"
                src={storyPortraitImage}
                srcSet={storyPortraitImageSrcSet}
                sizes="(max-width: 900px) 100vw, 380px"
                alt="Portrait of alMATHEMA founder"
                loading="lazy"
                decoding="async"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            <Box sx={{ maxWidth: 520 }}>
              <Typography variant="h3" sx={{ color: almathema.sapphire, fontStyle: 'italic', mb: 2 }}>
                Our Story
              </Typography>
              <Typography sx={{ color: almathema.sapphire, mb: 3 }}>
                alMATHEMA was founded by Liz Jimenez, a mathematics educator at Northern Virginia Community College, after years of working with students whose effort was undermined by gaps that no one had helped them see.
              </Typography>
              <Typography sx={{ color: almathema.sapphire, mb: 1 }}>
                “al” comes from al-jabr, meaning the reunion of broken parts.
              </Typography>
              <Typography sx={{ color: almathema.sapphire, mb: 1 }}>
                “mathema” means learning.
              </Typography>
              <Typography sx={{ color: almathema.sapphire, mb: 3 }}>
                Together, alMATHEMA represents lasting understanding through the union of what was once fragmented.
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ height: { xs: 220, md: 430 }, overflow: 'hidden' }}>
        <Box
          component="img"
          src={breakerTwoImage}
          alt="Work desk with books and devices"
          loading="lazy"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>

      <Box sx={{ backgroundColor: almathema.sunYellow, py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={5}>
            <Typography sx={{ color: almathema.sapphire, fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: { xs: '2.6rem', md: '4rem' }, lineHeight: 1 }}>
              What's Next
            </Typography>
            <Stack spacing={2}>
              <Typography sx={{ color: almathema.sapphire }}>
                alMATHEMA starts as a readiness diagnostic and evolves into a diagnostic learning system that supports students from course entry through exam preparation and beyond.
              </Typography>
              <Typography sx={{ color: almathema.sapphire }}>
                Every step of that growth is guided by the commitments that shaped the platform from the beginning: clarity over judgment, precision in insight, access for all students, and integrity in design.
              </Typography>
              <Button
                variant="contained"
                component="a"
                href="mailto:contact@almathema.com"
                sx={{ width: 'fit-content', backgroundColor: almathema.sapphire }}
              >
                Collaborate
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: almathema.white, color: almathema.sapphire, py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={6} alignItems="flex-start">
            <SiteFooterLinks variant="about" />
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default AboutUsPage;
